import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных сценариев пенсии
 */
const ComparisonSidebar = ({ 
  savedScenarios, 
  deleteSavedScenario, 
  loadSavedScenario 
}) => {
  // Если нет сохраненных сценариев
  if (!savedScenarios || savedScenarios.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение пенсионных сценариев
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные сценарии расчета пенсии для сравнения.
        </p>
        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400">
          Рассчитайте и сохраните несколько вариантов с разными параметрами, чтобы сравнить результаты.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение сценариев
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedScenarios.length} {savedScenarios.length === 1 ? 'сценарий' : (savedScenarios.length < 5 ? 'сценария' : 'сценариев')}
        </span>
      </div>

      <div className="space-y-4">
        {savedScenarios.map((scenario) => (
          <div 
            key={scenario.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {scenario.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedScenario(scenario.id)}
                  title="Загрузить сценарий"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedScenario(scenario.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Пенсия:</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(scenario.results.monthlyPension, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Замещение:</span>
                <span>
                  {formatNumber(scenario.results.replacementRate, 1)}%
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Выход на пенсию:</span>
                <span>
                  {scenario.results.retirementAge} {formatYearsLabel(scenario.results.retirementAge)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Тип:</span>
                <span className="capitalize">
                  {scenario.pensionType === 'mandatory' ? 'Страховая' : 
                   scenario.pensionType === 'voluntary' ? 'Накопительная' : 'Смешанная'}
                </span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 grid grid-cols-2 gap-2 text-xs">
              <div className="flex flex-col">
                <span className="text-gray-500 dark:text-gray-400">Зарплата</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(scenario.formData.salaryAmount, 0)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-500 dark:text-gray-400">Стаж</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{scenario.results.totalExperience} лет</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedScenarios.length > 1 && (
        <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <h4 className="text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-1">Сравнение сценариев</h4>
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            В таблице представлены разные варианты пенсионного обеспечения. Наибольший коэффициент замещения у сценария 
            «{getBestReplacementRateScenario()?.name || 'Нет данных'}» — {formatNumber(getBestReplacementRateScenario()?.results.replacementRate || 0, 1)}%.
          </p>
        </div>
      )}
    </div>
  );

  // Функция для получения сценария с наилучшим коэффициентом замещения
  function getBestReplacementRateScenario() {
    if (!savedScenarios.length) return null;
    
    return savedScenarios.reduce((best, current) => {
      return (current.results.replacementRate > best.results.replacementRate) ? current : best;
    }, savedScenarios[0]);
  }
};

// Вспомогательная функция для склонения слов
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ComparisonSidebar;